/**
 * Decides where to send an **existing** user who signed in from a shared Team
 * link. Pure function so it can be tested without mounting the navigator.
 *
 * Two link shapes reach this, and neither had a handler after a plain login
 * (TA-10773 item 0):
 *
 * - `/my-account/subscriptionPlanDetail/{uuid}/TeamDetails` — no token at all.
 *   `auth-stack.web.tsx` stores only the team UUID (and clears
 *   `invitation_token` so the preview screen won't query with a stale one), so
 *   the token-based team branch in `app-stack.web.tsx` never fires.
 * - `/invited-user?invitation_token=…&type=team` for a **shareable link**
 *   token. `TeamInvitationPreviewScreen` records
 *   `team_invite_auto_join = '0'`, and the token branch's comment says that
 *   case "falls through to the invite_signup useEffect which calls
 *   createUserRequest" — but that effect only runs when
 *   `invite_signup === '1'`, which a login never sets.
 *
 * Registrations are deliberately excluded: `decidePostSignupNavigation` owns
 * that path, and it also submits the join request. Both effects run on mount,
 * so acting here as well would clear the sessionStorage keys it still needs.
 */

import { isValidUuid } from './postSignupNavigation';

export type TeamLinkLoginSession = {
  /** sessionStorage `invite_signup` — '1' only after a registration */
  inviteSignup: string | null;
  /** sessionStorage `link_type` */
  linkType: string | null;
  /** sessionStorage `invitation_token` */
  invitationToken: string | null;
  /**
   * sessionStorage `team_invite_auto_join` — '1' email invite, '0' shareable
   * link, `null` when the preview screen never resolved the invitation.
   */
  teamInviteAutoJoin: string | null;
  /** sessionStorage `team_preview_uuid` */
  teamPreviewUuid: string | null;
  /** sessionStorage `task_or_project_id` — holds the team UUID for team links */
  taskOrProjectId: string | null;
};

export type TeamLinkLoginAction =
  /** UUID-only link — go straight to the team page, where NonMemberContent offers "Request to Join" */
  | { kind: 'team-detail'; teamUUId: string; keysToClear: string[] }
  /**
   * Shareable link token — resolve invitationDetails so
   * `decideTeamInviteAuthAction` can route it ('preview' lands on the same
   * team page). Going through the server also covers the case where the
   * preview screen never persisted the UUID.
   */
  | { kind: 'resolve-token'; invitationToken: string }
  /** Someone else's branch owns this load */
  | { kind: 'none' };

/**
 * Cleared once we navigate so a lingering `link_type` can't re-trigger the
 * redirect on every subsequent load in the same tab.
 */
const TEAM_LINK_LOGIN_KEYS = [
  'link_type',
  'task_or_project_id',
  'team_preview_uuid',
  'team_invite_auto_join',
  'invitation_token',
];

export const decideTeamLinkLoginNavigation = (
  s: TeamLinkLoginSession
): TeamLinkLoginAction => {
  if (s.linkType !== 'team') return { kind: 'none' };

  // Registration is decidePostSignupNavigation's job.
  if (s.inviteSignup === '1') return { kind: 'none' };

  if (s.invitationToken) {
    // Email invite — the accept mutation owns it.
    if (s.teamInviteAutoJoin === '1') return { kind: 'none' };

    // Shareable link token — needs approval, so land on the team page.
    if (s.teamInviteAutoJoin === '0') {
      return { kind: 'resolve-token', invitationToken: s.invitationToken };
    }

    // No flag: the preview screen never resolved the invitation, so we can't
    // tell the two apart. Leave the existing legacy accept path in charge.
    return { kind: 'none' };
  }

  const teamUUId = s.teamPreviewUuid ?? s.taskOrProjectId;
  if (!isValidUuid(teamUUId)) return { kind: 'none' };

  return {
    kind: 'team-detail',
    teamUUId,
    keysToClear: TEAM_LINK_LOGIN_KEYS,
  };
};
